import { useMemo, useState } from 'react';
import { LuLoaderCircle } from 'react-icons/lu';
import { MdCached, MdEdit, MdSchedule, MdSettings } from 'react-icons/md';

import { Button } from '@/components/Button';
import Loading from '@/components/Loading';
import illustMailOnly from '@/assets/images/candidate_mails/illust_mail_only.svg';
import illustPaperPlane from '@/assets/images/candidate_mails/illust_paper_plane.svg';
import type AgentApiCandidateMailsRecord from '@/types/serializers/AgentApiCandidateMailsRecord';
import { CANDIDATE_MAIL_PAGE_SIZE } from '../constants';
import useClientPagination from '../hooks/useClientPagination';
import {
  mailCreatorName,
  matchesMailKeyword,
  toBodyPreview,
  type CandidateMailBoxFolder,
} from '../lib/mailFolders';
import { formatMailListDate, formatMailScheduleListDate } from '../lib/scheduledAt';
import MailEmptyState from './MailEmptyState';
import MailListRow from './MailListRow';
import MailListToolbar from './MailListToolbar';
import MailStatusTag from './MailStatusTag';

type CandidateMailListProps = {
  folder: CandidateMailBoxFolder;
  mails: AgentApiCandidateMailsRecord[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  isSenderConfigured: boolean | null;
  onOpenMail: (mailId: number) => void;
  onCompose: () => void;
  onOpenSettings: () => void;
};

const emptyMessages: Record<CandidateMailBoxFolder, { title: string; description?: string }> = {
  sent: {
    title: '送信済みのメールはありません',
    description: 'メールを送信すると、ここに履歴が表示されます。',
  },
  scheduled: { title: '送信予定のメールはありません' },
  archive: { title: 'アーカイブされたメールはありません' },
};

function MailDate({
  mail,
  folder,
}: {
  mail: AgentApiCandidateMailsRecord;
  folder: CandidateMailBoxFolder;
}) {
  if (folder === 'scheduled') {
    return (
      <>
        <MdSchedule className="size-4 shrink-0" />
        {formatMailScheduleListDate(mail.scheduled_at)}
      </>
    );
  }
  if (mail.status === 'queued' || mail.status === 'processing') {
    return (
      <>
        <LuLoaderCircle className="size-5 shrink-0 animate-spin text-sub" />
        {mail.status === 'queued' ? '待機中' : '送信中'}
      </>
    );
  }
  if (mail.status === 'canceled')
    return <>{formatMailListDate(mail.canceled_at ?? mail.created_at)}</>;
  return <>{formatMailListDate(mail.sent_at ?? mail.created_at)}</>;
}

function MailStatus({ mail }: { mail: AgentApiCandidateMailsRecord }) {
  if (mail.status === 'failed') return <MailStatusTag color="red">送信エラー</MailStatusTag>;
  if (mail.status === 'canceled') return <MailStatusTag color="gray">キャンセル</MailStatusTag>;
  if (mail.status === 'completed' && mail.failed_count > 0) {
    return <MailStatusTag color="orange">一部失敗</MailStatusTag>;
  }
  return null;
}

export default function CandidateMailList({
  folder,
  mails,
  isLoading,
  isError,
  onRetry,
  isSenderConfigured,
  onOpenMail,
  onCompose,
  onOpenSettings,
}: CandidateMailListProps) {
  const [keyword, setKeyword] = useState('');
  const filteredMails = useMemo(
    () => mails.filter((mail) => matchesMailKeyword(mail, keyword)),
    [keyword, mails]
  );
  const { pagedItems, rangeLabel, canGoPrev, canGoNext, goPrev, goNext } = useClientPagination(
    filteredMails,
    CANDIDATE_MAIL_PAGE_SIZE,
    `${folder}:${keyword}`
  );

  if (isLoading) {
    return (
      <div className="flex w-full justify-center py-24">
        <Loading size="small" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-md px-md py-24">
        <p className="text-body-sm text-sub">
          メール一覧の取得に失敗しました。再度お試しください。
        </p>
        <Button type="button" color="secondary" size="md" onClick={onRetry}>
          <MdCached className="size-5" />
          再読み込み
        </Button>
      </div>
    );
  }

  if (isSenderConfigured === false) {
    return (
      <MailEmptyState
        illustration={<img src={illustMailOnly} alt="" width={108} height={98} />}
        title="メール機能を使うには設定が必要です"
        description="送信元アドレスや署名などを設定しましょう。"
        action={
          <Button type="button" color="primary" size="md" onClick={onOpenSettings}>
            <MdSettings className="size-5" />
            設定する
          </Button>
        }
      />
    );
  }

  if (mails.length === 0) {
    return (
      <MailEmptyState
        illustration={
          folder === 'sent' ? (
            <img src={illustPaperPlane} alt="" width={137} height={126} />
          ) : undefined
        }
        title={emptyMessages[folder].title}
        description={emptyMessages[folder].description}
        action={
          folder === 'sent' && (
            <Button type="button" color="primary" size="md" onClick={onCompose}>
              <MdEdit className="size-5" />
              メールを作成する
            </Button>
          )
        }
      />
    );
  }

  return (
    <div className="flex w-full flex-col">
      <MailListToolbar
        keyword={keyword}
        onKeywordChange={setKeyword}
        placeholder="宛先・件名・本文・作成者で検索"
        rangeLabel={rangeLabel}
        canGoPrev={canGoPrev}
        canGoNext={canGoNext}
        onPrev={goPrev}
        onNext={goNext}
      />
      {pagedItems.length === 0 ? (
        <p className="px-lg py-xl text-center text-body-sm text-sub">該当するメールはありません</p>
      ) : (
        <ul className="flex flex-col">
          {pagedItems.map((mail) => (
            <MailListRow
              key={mail.id}
              status={<MailStatus mail={mail} />}
              subject={mail.subject?.trim() || '（件名なし）'}
              snippet={toBodyPreview(mail.body)}
              author={mailCreatorName(mail)}
              date={<MailDate mail={mail} folder={folder} />}
              highlighted={mail.status === 'queued' || mail.status === 'processing'}
              onClick={() => onOpenMail(mail.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
